import { useState } from 'react'
import { Grid } from 'semantic-ui-react'
import moment from 'moment'

import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { centsToDollars } from '@/utils/currencyHelpers'
import { updateUserTransaction } from '../transactions.slice'
import { TransactionOrSplit } from '@/reducers/admin/allTransactions.slice'
import { Button, TextArea, Text, Alert, Modal } from '@/base'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useYearFromParams } from '@/features/bookkeeping/final-review/hooks'

interface TransactionNoteModalProps {
  open: boolean
  close: (updated: boolean) => Promise<void>
  transaction: TransactionOrSplit
}
const DeprecatedTransactionNoteModal = ({
  open,
  close,
  transaction,
}: TransactionNoteModalProps) => {
  const dispatch = useAppDispatch()
  const yearEndYear = useYearFromParams()
  const [transactionNote, setTransactionNote] = useState(
    transaction.notes || ''
  )
  const [updating, setUpdating] = useState(false)
  const [error, setError] = useState(false)

  const cancel = () => close(false)

  const saveNote = async () => {
    setUpdating(true)
    setError(false)

    const updated = await updateUserTransaction(
      transaction.id,
      {
        notes: transactionNote,
        notesLastUpdatedAt: new Date().toISOString(),
      },
      yearEndYear
    )(dispatch)

    if (updated) {
      setUpdating(false)
      await close(true)
    } else {
      setError(true)
    }
  }

  return (
    <Modal
      size="tiny"
      className="transactionNoteModalProps"
      open={open}
      onClose={cancel}
      closeIcon
    >
      <Modal.Header>Leave us a note</Modal.Header>
      <Modal.Content>
        <Grid>
          {error && (
            <Grid.Row>
              <Grid.Column>
                <Alert type="error">
                  There was an error saving this note! Please try again.
                </Alert>
              </Grid.Column>
            </Grid.Row>
          )}
          <Grid.Row>
            <Grid.Column>
              <Text as="bodyMd">
                <i>
                  Let us know any further details on this transaction and what
                  it was for. We&#39;ll help you categorize it accordingly!
                </i>
              </Text>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column>
              <Text as="bodyMd">
                <b>Date:</b>{' '}
                {moment
                  .utc(transaction?.date)
                  .format(DATE_FORMATS.DISPLAY_SHORT)}
              </Text>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column>
              <Text as="bodyMd">
                <b>Description:</b> {transaction.description}
              </Text>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column>
              <Text as="bodyMd">
                <b>Amount:</b>{' '}
                <CurrencyFormatLabel
                  value={centsToDollars(transaction.amountInCents)}
                />
              </Text>
            </Grid.Column>
          </Grid.Row>

          <Grid.Row>
            <Grid.Column>
              <TextArea
                label="Note"
                rows={2}
                placeholder="If unsure, leave a note about what this transaction is for and we’ll help you categorize!"
                value={transactionNote}
                onChange={(value) => setTransactionNote(value)}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <Button variant="secondary" onClick={cancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={saveNote}
          disabled={updating}
          loading={updating}
        >
          Save Note and Close
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

export default DeprecatedTransactionNoteModal
